import React from 'react'

import clsx from 'clsx'
import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { RadioGroup } from '@headlessui/react'

import { DeviceOrderData } from '@/lib/schemes'
import { Device, OrderEstimate } from '@/lib/types'

import useUserInfo from '@/hooks/users/useUserInfo'

import CreditCardSelect from '@/components/CreditCardSelect'
import Icon from '@/components/Icon'
import Label from '@/components/Label'
import Tooltip from '@/components/Tooltip'
import { OrderAdvanceBlock } from '@/components/features/shop/OrderAdvanceBlock'
import FormField from '@/components/form/FormField'

import { FundsFlowInfoInstallment } from './FundsFlowInfoInstallment'
import { FundsFlowInfoPerTH } from './FundsFlowInfoPerTH'
import OrderTotalInfo from './OrderTotalInfo'

const BONUS_DATA = [
  { title: 'Не списывать бонусы', value: false },
  { title: 'Списать бонусы', value: true }
]

export const DeviceOrderDetails = ({
  form,
  device,
  estimate,
  isLoading,
  isInstallment
}: {
  form: UseFormReturn<DeviceOrderData>
  device: Device
  isInstallment?: boolean
  estimate: OrderEstimate | undefined
  isLoading: boolean
}) => {
  const { formatMessage } = useIntl()
  const {
    watch,
    register,
    formState: { errors }
  } = form

  const { bonusesSpent, canPayWithBalance, canPayWithBonuses } = estimate || {}

  const isPaidByBalance = watch('paymentMethod') === 'balance'

  return (
    <div className="flex flex-col gap-4 tablet:gap-5">
      {isInstallment && (
        <FundsFlowInfoInstallment {...{ estimate, device, isLoading }} />
      )}
      <FundsFlowInfoPerTH {...{ estimate, device, isLoading }} />

      {!isPaidByBalance && (
        <OrderAdvanceBlock {...{ device, form, estimate, isLoading }} />
      )}

      <FormField className="gap-1" error={errors.paymentMethod}>
        <Label className="text-x-base leading-x-md">
          <FormattedMessage
            {...{
              id: 'dialog.order.payment',
              defaultMessage: 'Оплата'
            }}
          />
        </Label>
        <CreditCardSelect
          className="w-full"
          value={watch('paymentMethod')}
          {...register('paymentMethod')}
          hasBalance={!!canPayWithBalance}
          placeholder={formatMessage({
            id: 'button.select',
            defaultMessage: 'Выберите'
          })}
        />
      </FormField>

      {canPayWithBonuses && <BonusPayRadio {...{ form, bonusesSpent }} />}

      <OrderTotalInfo {...{ device, form, estimate, isLoading }} />
    </div>
  )
}

const BonusPayRadio = ({
  bonusesSpent,
  form: { watch, setValue }
}: {
  bonusesSpent: number | undefined
  form: UseFormReturn<DeviceOrderData>
}) => {
  const { bonusBalance } = useUserInfo()

  return (
    <RadioGroup
      className="space-y-3"
      onChange={(value) => setValue('useBonusBalance', value)}
      value={watch('useBonusBalance')}
    >
      {BONUS_DATA.map(({ title, value }, index) => (
        <RadioGroup.Option className="space-y-3" key={index} value={value}>
          {({ checked }) => (
            <>
              <div className="flex items-center">
                <div className="relative flex cursor-pointer items-center self-start">
                  <div
                    className={clsx(
                      'relative h-6 w-6 cursor-pointer appearance-none rounded-full border border-slate-300 transition-all hover:border-blue-500',
                      checked &&
                        'border-blue-500 bg-blue-600 before:bg-blue-600 '
                    )}
                  />
                  <div
                    className={clsx(
                      'pointer-events-none absolute inset-0 left-1/2 top-1/2 block h-2 w-2 flex-none -translate-x-1/2 -translate-y-1/2',
                      'rounded-full bg-white opacity-0 transition-opacity',
                      checked && 'opacity-100'
                    )}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="ml-2.5 cursor-pointer select-none text-x-base leading-x-base">
                    <FormattedMessage
                      {...{
                        id: `dialog.order.bonus.${value}`,
                        defaultMessage: title
                      }}
                    />
                  </label>
                  {checked && value && (
                    <span
                      className={clsx(
                        'inline-flex items-center justify-center rounded-md',
                        'gap-1.5 bg-slate-100 px-2.5 py-1 text-x-sm leading-x-sm'
                      )}
                    >
                      <FormattedMessage
                        {...{
                          id: 'dialog.order.bonus.hint',
                          defaultMessage: 'Будет списано {spent} из {balance}',
                          values: {
                            spent: bonusesSpent,
                            balance: bonusBalance
                          }
                        }}
                      />
                      <Tooltip
                        className="max-w-[307px] text-x-base"
                        contentComponent={
                          <FormattedMessage
                            {...{
                              id: 'finances.balance.bonuses',
                              defaultMessage:
                                'Оплачивайте бонусами до 10% при покупке оборудования для майнинга'
                            }}
                          />
                        }
                      >
                        <Icon
                          className="inline h-4 w-4 flex-none cursor-pointer text-secondary-500"
                          name="exclamation-circle-16"
                        />
                      </Tooltip>
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </RadioGroup.Option>
      ))}
    </RadioGroup>
  )
}
